import { cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../cn';
import { isDevelopment } from '../../internal/is-development';
import { resolveRender, type RenderProp } from '../../internal/render-seam';

/**
 * The typography role vocabulary, as camelCase handles onto the kebab-case role
 * names. Callers may pass either — the type is the union of the *values* — but
 * the map gives editors something to autocomplete and gives dependents a stable
 * reference (`TYPOGRAPHY_VARIANTS.bodyMRegular`) instead of a bare string.
 */
export const TYPOGRAPHY_VARIANTS = {
  bodyL: 'body-l',
  bodyLSemibold: 'body-l-semibold',
  bodyMRegular: 'body-m-regular',
  bodyMMedium: 'body-m-medium',
  bodyMSemibold: 'body-m-semibold',
  bodyS: 'body-s',
  bodySMedium: 'body-s-medium',
  bodySSemibold: 'body-s-semibold',
  bodyXs: 'body-xs',
  bodyXsSemibold: 'body-xs-semibold',
  ctaRegularL: 'cta-regular-l',
  ctaRegularM: 'cta-regular-m',
  ctaRegularS: 'cta-regular-s',
  ctaMediumXs: 'cta-medium-xs',
  ctaStrongL: 'cta-strong-l',
  ctaStrongM: 'cta-strong-m',
  ctaStrongS: 'cta-strong-s',
  d1: 'd1',
  d2: 'd2',
  d3: 'd3',
  d4: 'd4',
  d5: 'd5',
  d6: 'd6',
  d7: 'd7',
  eyebrowL: 'eyebrow-l',
  eyebrowLSemibold: 'eyebrow-l-semibold',
  eyebrowSRetina: 'eyebrow-s-retina',
  eyebrowSSemibold: 'eyebrow-s-semibold',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  h7: 'h7',
  monoLSemibold: 'mono-l-semibold',
  monoMMedium: 'mono-m-medium',
  monoMRegular: 'mono-m-regular',
  monoMSemibold: 'mono-m-semibold',
  monoSRegular: 'mono-s-regular',
  subheadL: 'subhead-l',
  subheadM: 'subhead-m',
  subheadS: 'subhead-s',
} as const;

export type TypographyVariantsType = (typeof TYPOGRAPHY_VARIANTS)[keyof typeof TYPOGRAPHY_VARIANTS];

/**
 * A CSS gradient is a background image, so gradient text is painted as a
 * background clipped to the glyphs. Each value below is an arbitrary Tailwind
 * background-image value wrapping a `--gradients-*` token, which is necessary:
 * Tailwind has no `bg-image-*` namespace to generate these from.
 *
 * **Every class name here is written out in full, and must stay that way.**
 * Deriving them from `TYPOGRAPHY_GRADIENTS` with a template literal reads far
 * better and generates nothing: Tailwind extracts candidates by scanning source
 * text, so a name assembled at runtime is invisible to it and no gradient utility
 * is ever emitted. This feature was shipped broken for exactly that reason.
 *
 * Worse, an incomplete class name survives into the published bundle, where a
 * consumer's `@source` points Tailwind straight at it. The extractor takes it for
 * a real candidate and emits a `background-image` whose value still holds the
 * placeholder — which is not valid CSS. Lightning CSS rejects it: a warning in
 * `next build`, a hard HTTP 500 on every request in `next dev`, so the build
 * stays green while local development is broken.
 *
 * **So no prose in this package may spell out a bracket-syntax class whose value
 * is not a real token.** Not the interpolated form, and not a `*`/`NAME`/`…`
 * stand-in either — the extractor cannot tell prose from code, and a docblock is
 * copied verbatim into both `sourcesContent` of the emitted `.js.map` and the
 * generated `.d.ts`. A consumer whose glob reaches either one gets the invalid
 * rule. Describe the shape in words, as above. `build.packaging.test.ts` compiles
 * Tailwind against the whole of `dist` — maps and declarations included — and
 * fails on any candidate that emits CSS Lightning CSS rejects.
 */
const GRADIENT_VARIANTS = {
  'blue-primary': 'bg-[image:var(--gradients-blue-primary)]',
  'blue-secondary': 'bg-[image:var(--gradients-blue-secondary)]',
  'blue-tertiary': 'bg-[image:var(--gradients-blue-tertiary)]',
  'blue-quaternary': 'bg-[image:var(--gradients-blue-quaternary)]',
  'blue-balance': 'bg-[image:var(--gradients-blue-balance)]',
  'blue-card-active': 'bg-[image:var(--gradients-blue-card-active)]',
  'white-primary': 'bg-[image:var(--gradients-white-primary)]',
  'white-on-surface': 'bg-[image:var(--gradients-white-on-surface)]',
  'mint-active': 'bg-[image:var(--gradients-mint-active)]',
  'mint-primary': 'bg-[image:var(--gradients-mint-primary)]',
  'lime-primary': 'bg-[image:var(--gradients-lime-primary)]',
  'lavender-primary': 'bg-[image:var(--gradients-lavender-primary)]',
  'surface-fade': 'bg-[image:var(--gradients-surface-fade)]',
} as const;

/**
 * Gradient fills available to text. Every name here is a `--gradients-*` token;
 * the component's tests assert that, so a gradient cannot be offered that the
 * token layer cannot paint. Derived from `GRADIENT_VARIANTS` so the two cannot
 * drift.
 */
export const TYPOGRAPHY_GRADIENTS = Object.keys(GRADIENT_VARIANTS) as (keyof typeof GRADIENT_VARIANTS)[];

export type TypographyGradientsType = keyof typeof GRADIENT_VARIANTS;

/**
 * Display roles need vertical breathing room when gradient-filled: `bg-clip-text`
 * clips to the glyph box, which crops descenders. Negative margin plus matching
 * padding buys back an eighth of the line height without moving the text.
 *
 * Written out per role for the same reason as `GRADIENT_VARIANTS`: interpolating
 * the role into the token name generates no CSS.
 */
const DESCENDER_PADDING = {
  d1: 'mb-[calc((var(--leading-height-d1)/8)*-1)] pb-[calc(var(--leading-height-d1)/8)]',
  d2: 'mb-[calc((var(--leading-height-d2)/8)*-1)] pb-[calc(var(--leading-height-d2)/8)]',
  d3: 'mb-[calc((var(--leading-height-d3)/8)*-1)] pb-[calc(var(--leading-height-d3)/8)]',
  d4: 'mb-[calc((var(--leading-height-d4)/8)*-1)] pb-[calc(var(--leading-height-d4)/8)]',
  d5: 'mb-[calc((var(--leading-height-d5)/8)*-1)] pb-[calc(var(--leading-height-d5)/8)]',
  d6: 'mb-[calc((var(--leading-height-d6)/8)*-1)] pb-[calc(var(--leading-height-d6)/8)]',
  d7: 'mb-[calc((var(--leading-height-d7)/8)*-1)] pb-[calc(var(--leading-height-d7)/8)]',
} as const;

const gradientDescenderPadding = (role: keyof typeof DESCENDER_PADDING) => ({
  gradient: [...TYPOGRAPHY_GRADIENTS],
  textStyle: role,
  className: DESCENDER_PADDING[role],
});

/**
 * Every role sets four token utilities — family, size, line height, and tracking
 * — because the token layer defines all four per role and a role is only itself
 * with all of them.
 *
 * **Font families are translated, not copied.** maker-ds names Twilio Sans Text
 * `--font-serif` and Twilio Sans Display `--font-sans`. Strix renames those to
 * `--font-sans` and `--font-display`, because calling a sans-serif face "serif"
 * misleads every reader. So upstream `font-serif` becomes `font-sans` here, and
 * upstream `font-sans` (the display roles) becomes `font-display`. Copying
 * upstream classes across verbatim silently drops the family; the tests guard
 * against exactly that.
 */
export const typographyVariants = cva('break-words hyphens-auto normal-nums', {
  variants: {
    textStyle: {
      // Body
      'body-l': 'font-sans text-size-body-large leading-height-body-large font-body-large-regular tracking-body-large',
      'body-l-semibold':
        'font-sans text-size-body-large leading-height-body-large font-body-large-semibold tracking-body-large',
      'body-m-regular':
        'font-sans text-size-body-medium leading-height-body-medium font-body-medium-regular tracking-body-medium',
      'body-m-medium':
        'font-sans text-size-body-medium leading-height-body-medium font-body-medium-medium tracking-body-medium',
      'body-m-semibold':
        'font-sans text-size-body-medium leading-height-body-medium font-body-medium-semibold tracking-body-medium',
      'body-s': 'font-sans text-size-body-small leading-height-body-small font-body-small-regular tracking-body-small',
      'body-s-medium':
        'font-sans text-size-body-small leading-height-body-small font-body-small-medium tracking-body-small',
      'body-s-semibold':
        'font-sans text-size-body-small leading-height-body-small font-body-small-semibold tracking-body-small',
      'body-xs':
        'font-sans text-size-body-xsmall leading-height-body-xsmall font-body-xsmall-regular tracking-body-xsmall',
      'body-xs-semibold':
        'font-sans text-size-body-xsmall leading-height-body-xsmall font-body-xsmall-semibold tracking-body-xsmall',
      // CTA
      'cta-regular-l':
        'font-sans text-size-cta-regular-large leading-height-cta-regular-large font-cta-regular-large tracking-cta-regular-large',
      'cta-regular-m':
        'font-sans text-size-cta-regular-medium leading-height-cta-regular-medium font-cta-regular-medium tracking-cta-regular-medium',
      'cta-regular-s':
        'font-sans text-size-cta-regular-small leading-height-cta-regular-small font-cta-regular-small tracking-cta-regular-small',
      'cta-medium-xs':
        'font-sans text-size-cta-medium-xsmall leading-height-cta-medium-xsmall font-cta-medium-xsmall tracking-cta-medium-xsmall',
      'cta-strong-l':
        'font-sans text-size-cta-strong-large leading-height-cta-strong-large font-cta-strong-large tracking-cta-strong-large',
      'cta-strong-m':
        'font-sans text-size-cta-strong-medium leading-height-cta-strong-medium font-cta-strong-medium tracking-cta-strong-medium',
      'cta-strong-s':
        'font-sans text-size-cta-strong-small leading-height-cta-strong-small font-cta-strong-small tracking-cta-strong-small',
      // Display — the only roles in the Twilio Sans Display face.
      d1: 'font-display text-size-d1 leading-height-d1 font-d1 tracking-d1',
      d2: 'font-display text-size-d2 leading-height-d2 font-d2 tracking-d2',
      d3: 'font-display text-size-d3 leading-height-d3 font-d3 tracking-d3',
      d4: 'font-display text-size-d4 leading-height-d4 font-d4 tracking-d4',
      d5: 'font-display text-size-d5 leading-height-d5 font-d5 tracking-d5',
      d6: 'font-display text-size-d6 leading-height-d6 font-d6 tracking-d6',
      d7: 'font-display text-size-d7 leading-height-d7 font-d7 tracking-d7',
      // Eyebrow — uppercase is part of the role, so it lives in the class list
      // rather than an inline style a caller cannot override.
      'eyebrow-l':
        'font-sans text-size-eyebrow-large leading-height-eyebrow-large font-eyebrow-large-regular tracking-eyebrow-large uppercase',
      'eyebrow-l-semibold':
        'font-sans text-size-eyebrow-large leading-height-eyebrow-large font-eyebrow-large-semibold tracking-eyebrow-large uppercase',
      'eyebrow-s-retina':
        'font-sans text-size-eyebrow-small leading-height-eyebrow-small font-eyebrow-small-regular tracking-eyebrow-small uppercase',
      'eyebrow-s-semibold':
        'font-sans text-size-eyebrow-small leading-height-eyebrow-small font-eyebrow-small-semibold tracking-eyebrow-small uppercase',
      // Headline
      h1: 'font-sans text-size-h1 leading-height-h1 font-h1 tracking-h1',
      h2: 'font-sans text-size-h2 leading-height-h2 font-h2 tracking-h2',
      h3: 'font-sans text-size-h3 leading-height-h3 font-h3 tracking-h3',
      h4: 'font-sans text-size-h4 leading-height-h4 font-h4 tracking-h4',
      h5: 'font-sans text-size-h5 leading-height-h5 font-h5 tracking-h5',
      h6: 'font-sans text-size-h6 leading-height-h6 font-h6 tracking-h6',
      h7: 'font-sans text-size-h7 leading-height-h7 font-h7 tracking-h7',
      // Mono
      'mono-l-semibold':
        'font-mono text-size-mono-large leading-height-mono-large font-mono-large-semibold tracking-mono-large',
      'mono-m-semibold':
        'font-mono text-size-mono-medium leading-height-mono-medium font-mono-medium-semibold tracking-mono-medium',
      'mono-m-medium':
        'font-mono text-size-mono-medium leading-height-mono-medium font-mono-medium-medium tracking-mono-medium',
      'mono-m-regular':
        'font-mono text-size-mono-medium leading-height-mono-medium font-mono-medium-regular tracking-mono-medium',
      'mono-s-regular':
        'font-mono text-size-mono-small leading-height-mono-small font-mono-small-regular tracking-mono-small',
      // Subhead
      'subhead-l':
        'font-sans text-size-subhead-large leading-height-subhead-large font-subhead-large tracking-subhead-large',
      'subhead-m':
        'font-sans text-size-subhead-medium leading-height-subhead-medium font-subhead-medium tracking-subhead-medium',
      'subhead-s':
        'font-sans text-size-subhead-small leading-height-subhead-small font-subhead-small tracking-subhead-small',
    },
    gradient: GRADIENT_VARIANTS,
  },
  compoundVariants: [
    gradientDescenderPadding('d1'),
    gradientDescenderPadding('d2'),
    gradientDescenderPadding('d3'),
    gradientDescenderPadding('d4'),
    gradientDescenderPadding('d5'),
    gradientDescenderPadding('d6'),
    gradientDescenderPadding('d7'),
  ],
});

/** Gradient text needs a transparent foreground for the clipped background to show. */
const gradientBaseClasses = 'text-transparent! bg-clip-text';

/**
 * Elements `as` can name.
 *
 * The list is deliberately not "every intrinsic tag" — `as` is for elements whose job is to hold
 * text, and a `<button>` or `<input>` here would invite a caller to reach for a type primitive
 * where they want a control. Anything outside the list still goes through `render`.
 *
 * **Lists, `code`, `pre` and `blockquote` are here because their absence was a real defect, not a
 * boundary.** A styled `<ul>` had to be written `render={<ul />}` while a styled `<p>` was
 * `as="p"`, so the narrower seam was also the prettier one and the choice between them turned on
 * an allowlist the caller could not see. `pre` in particular is text-holding by definition, and
 * `code` had already forced one component (`ToolCall`) to restate a whole type role by hand rather
 * than nest a span inside a bare `<code>`.
 *
 * `pre` keeps its own `white-space` — the base class list sets no `whitespace-*`, so the UA
 * stylesheet's `pre` value stands and a caller who wants wrapping passes `whitespace-pre-wrap`.
 */
export type AllowedTags =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'span'
  | 'label'
  | 'p'
  | 'div'
  | 'ul'
  | 'ol'
  | 'li'
  | 'dl'
  | 'dt'
  | 'dd'
  | 'code'
  | 'pre'
  | 'blockquote'
  | 'figcaption'
  | 'small'
  | 'strong'
  | 'em';

/** What the `render` function form is told about the component. */
export interface TypographyRenderState {
  variant: TypographyVariantsType;
  gradient: TypographyGradientsType | null;
}

export type TypographyProps<C extends AllowedTags = 'span'> = {
  /**
   * HTML element to render.
   *
   * **`span` is inline.** A margin or block-layout utility (`mb-*`, `block`, `flex`, `w-full`, ...)
   * passed through `className` computes on an inline box and does nothing — not a Tailwind bug,
   * just CSS. Pass `as="div"` or `as="p"` when the caller needs the element to actually take
   * margin or participate in block layout, rather than adding a layout utility and finding it inert.
   *
   * @default 'span'
   */
  as?: C;
  /**
   * Render into a caller-supplied element, or a function returning one.
   *
   * **What `as` cannot do.** `as` substitutes an intrinsic tag from {@link AllowedTags}; this
   * substitutes a *component*, which is the case that actually comes up: a router's `Link`, a
   * `BreadcrumbLink`, a Base UI trigger. Without it the only options are wrapping — which splits
   * the semantics and the type role across two elements — or abandoning the component and
   * hand-applying the classes, which is what the token layer exists to prevent.
   *
   * It is also the seam for an element `as` deliberately omits — a control, or an `<a>` needing
   * `href`. Reach for `as` first; if the tag is not in the list, that is the signal to use this.
   *
   * - `render={<a href="/x" />}` is cloned, with the element's own `className` merged last so a
   *   caller's utility still beats the role.
   * - `render={(props, { variant }) => <a {...props} />}` is called and its result used as is. The
   *   function owns the element; nothing is merged over the top.
   *
   * `render` wins if `as` is also passed.
   */
  render?: RenderProp<TypographyRenderState>;
  /**
   * Typography role.
   * @default 'body-m-regular'
   */
  variant?: TypographyVariantsType;
  /** Optional gradient fill. Display roles also gain descender padding. */
  gradient?: TypographyGradientsType | null;
} & React.ComponentPropsWithoutRef<C>;

/**
 * Renders text in one of the token layer's typography roles.
 *
 * Element choice is separate from visual role on purpose: a heading that should
 * read as small body text is `as="h2" variant="body-s"`, which keeps the
 * document outline correct without forcing the design to follow it.
 *
 * **Two seams, because they do different jobs.** `as` names an intrinsic tag and stays the short
 * path for the common case. `render` takes an element and is the house convention — the same seam
 * `Button`, `SidebarMenuButton` and `NavigationMenuLink` use — and it is the only one that can
 * compose with another *component*. This used to carry a comment calling `as` a deliberate
 * exception to that convention on the grounds that `Typography` predates it; that was a reason it
 * happened, not a reason to keep it, and it left every "styled text that is also a link" case with
 * no expressible form.
 *
 * @example <Typography as="h1" variant="d1">Welcome</Typography>
 * @example <Typography variant="body-s" render={<a href="/pricing" />}>Pricing</Typography>
 * @example <Typography variant="body-s" className="text-text-secondary">Detail</Typography>
 */
/**
 * Utilities that compute to nothing on an inline box.
 *
 * Vertical margin, width/height and block-layout participation are all ignored on an inline
 * element, so these are exactly the classes a caller passes and then cannot find the effect of.
 * Horizontal margin and padding are NOT here: `mx-*`/`px-*` do apply to an inline box.
 *
 * `display` utilities are excluded too, because passing `block` or `flex` is a caller *fixing* the
 * problem in CSS rather than hitting it — `<Typography className="block mb-8">` renders correctly.
 */
const INERT_ON_INLINE =
  /(?<![\w-])(?:(?:mt|mb|my|h|min-h|max-h|w|min-w|max-w|size|space-y|self|order|basis|col-span|row-span)-|(?:grow|shrink)(?![\w-]))/;

/** A `display` utility in the caller's classes makes the box non-inline, so the warning is moot. */
const SETS_DISPLAY =
  /(?<![\w-])(?:block|inline-block|flex|inline-flex|grid|inline-grid|table|contents|flow-root|hidden)(?![\w-])/;

const TypographyRenderFn = <C extends AllowedTags = 'span'>(
  {
    as,
    render,
    children = null,
    variant = TYPOGRAPHY_VARIANTS.bodyMRegular,
    gradient = null,
    className,
    ...props
  }: TypographyProps<C>,
  ref: React.ComponentPropsWithRef<C>['ref']
) => {
  const classes = cn(
    gradient ? gradientBaseClasses : null,
    typographyVariants({ textStyle: variant, gradient }),
    className
  );

  /**
   * `span` is the default, and a margin utility on an inline box does nothing.
   *
   * This is the migration trap the component could not warn about: `<p className="mb-8">` becomes
   * `<Typography className="mb-8">`, the class survives review because it is a perfectly good
   * class, and the margin silently evaporates. A consumer's /upload-success page shipped two
   * sentences on one line, and a screenshot — not 2000 passing tests — is what caught it.
   *
   * A warning rather than a changed default: making `body-*` roles render `<p>` would alter the
   * markup of every existing call site, and `Typography` is used inline often enough that the
   * default is right. Not a lint rule either, because a consumer need not run Strix's ESLint
   * config — this fires wherever the component actually renders, and compiles out of production.
   */
  if (isDevelopment && !as && !render && typeof className === 'string') {
    if (INERT_ON_INLINE.test(className) && !SETS_DISPLAY.test(className)) {
      console.warn(
        `Typography: "${className}" includes a utility that does nothing on an inline element, and this renders a <span> by default. ` +
          'Pass `as="p"` or `as="div"` (or add `block`) if the element should take vertical margin or participate in block layout.'
      );
    }
  }

  if (render) {
    return resolveRender(render, { ...props, ref, className: classes }, { variant, gradient }, children ?? undefined);
  }

  const Component = (as ?? 'span') as React.ElementType;

  return (
    <Component {...props} ref={ref} className={classes}>
      {children}
    </Component>
  );
};

export const Typography = React.forwardRef(TypographyRenderFn) as <C extends AllowedTags = 'span'>(
  props: TypographyProps<C> & { ref?: React.ComponentPropsWithRef<C>['ref'] }
) => React.ReactElement | null;

(Typography as { displayName?: string }).displayName = 'Typography';
